import { useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import "./CustomerPaginatedTable.css";

/**
 * PaginatedTable — a reusable paginated table for the CPV design system.
 *
 * Props:
 * ┌──────────────┬──────────────────────────────────────────────────────────────────────────┐
 * │ columns      │ Array<{ key: string, label: string, width?: string, render?: fn }>       │
 * │              │   key    — field name on the row object (used if render is omitted)      │
 * │              │   label  — column header text                                            │
 * │              │   width  — CSS fr / % / px string, e.g. "20%". Defaults to "1fr".       │
 * │              │   render — optional (row, rowIndex) => ReactNode for custom cells        │
 * ├──────────────┼──────────────────────────────────────────────────────────────────────────┤
 * │ rows         │ Array of plain objects                                                   │
 * ├──────────────┼──────────────────────────────────────────────────────────────────────────┤
 * │ loading      │ boolean — shows spinner overlay                                          │
 * ├──────────────┼──────────────────────────────────────────────────────────────────────────┤
 * │ meta         │ { page, pageSize, totalResults }                                         │
 * ├──────────────┼──────────────────────────────────────────────────────────────────────────┤
 * │ onPageChange │ (newPage: number) => void                                                │
 * ├──────────────┼──────────────────────────────────────────────────────────────────────────┤
 * │ emptyText    │ string — shown when rows is empty. Default: "No data found."             │
 * ├──────────────┼──────────────────────────────────────────────────────────────────────────┤
 * │ rowKey       │ string | ((row, index) => string|number) — unique key per row            │
 * │              │ Default: uses row index                                                  │
 * └──────────────┴──────────────────────────────────────────────────────────────────────────┘
 *
 * ─── Usage examples ──────────────────────────────────────────────────────────
 *
 * // 1. Basic order table (mirrors your OrderTab)
 * <PaginatedTable
 *   columns={[
 *     { key: "orderId",   label: "Order ID",  width: "25%" },
 *     { key: "channel",   label: "Channel",   width: "35%" },
 *     { key: "amount",    label: "Amount",    width: "20%" },
 *     {
 *       key: "status",
 *       label: "Status",
 *       width: "20%",
 *       render: (row) => (
 *         <span className={`cpv__order-badge cpv__order-badge--${row.status.toLowerCase()}`}>
 *           {row.status}
 *         </span>
 *       ),
 *     },
 *   ]}
 *   rows={tabOrders.map((o) => ({
 *     orderId: o?.baseOrderResponse?.orderId,
 *     channel: o?.baseOrderResponse?.salesChannel,
 *     amount:  `${o?.baseOrderResponse?.amountPaid}`,
 *     status:  o?.baseOrderResponse?.paymentStatus,
 *   }))}
 *   loading={tabLoading}
 *   meta={tabOrdersMeta}
 *   onPageChange={fetchOrders}
 *   rowKey="orderId"
 * />
 *
 * // 2. Coupon table (mirrors your CouponsTab)
 * <PaginatedTable
 *   columns={[
 *     { key: "code",     label: "Coupon Code",  width: "15%" },
 *     { key: "offer",    label: "Offer Name",   width: "38%" },
 *     { key: "discount", label: "Discount",     width: "15%" },
 *     { key: "validTo",  label: "Valid Till",   width: "20%" },
 *     {
 *       key: "status",
 *       label: "Status",
 *       width: "12%",
 *       render: (row) => (
 *         <span className={`cpv__coupon-status ${row.status?.toLowerCase() === "active" ? "active" : "used"}`}>
 *           {row.status}
 *         </span>
 *       ),
 *     },
 *   ]}
 *   rows={tabCoupons.map((c) => ({
 *     code:     c?.couponCode,
 *     offer:    c?.offerName,
 *     discount: c?.discountType === "FLAT_OFF" ? `${c?.discountValue} off` : `${c?.discountValue}% off`,
 *     validTo:  formatDate(c?.validTo),
 *     status:   c?.status,
 *   }))}
 *   loading={tabLoading}
 *   meta={tabCouponsMeta}
 *   onPageChange={fetchCoupons}
 *   rowKey="code"
 * />
 */

const CustomerPaginatedTable = ({
  columns = [],
  rows = [],
  loading = false,
  meta = { page: 0, pageSize: 10, totalResults: 0 },
  onPageChange,
  emptyText = "No data found.",
  rowKey,
  onRowClick,
  showPagination = true,
}) => {
  const { page, pageSize, totalResults } = meta;
  const start = totalResults === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalResults);

  // Build the CSS grid-template-columns from the columns array
  const gridCols = columns?.map((c) => c.width || "1fr").join(" ");

  const getKey = (row, index) => {
    if (!rowKey) return index;
    if (typeof rowKey === "function") return rowKey(row, index);
    return row[rowKey] ?? index;
  };

  // ── Initial full-page loader ───────────────────────────────────────────────
  if (loading && rows?.length === 0) {
    return (
      <div className="cpt__empty">
        <div className="cpt__spinner" />
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!loading && rows?.length === 0) {
    return <div className="cpt__empty">{emptyText}</div>;
  }

  // ── Table ──────────────────────────────────────────────────────────────────
  return (
    <div className="cpt__wrap">
      {/* Table Content Wrapper for Scrolling */}
      <div className="cpt__table-scroll">
        {/* Header */}
        <div className="cpt__header" style={{ gridTemplateColumns: gridCols }}>
          {columns?.map((col) => (
            <span key={col?.key} className="cpt__header-cell">
              {col?.label}
            </span>
          ))}
        </div>

        {/* Pagination loader overlay */}
        {loading ? (
          <div className="cpt__page-loader">
            <div className="cpt__spinner" />
          </div>
        ) : (
          <div className="cpt__body">
            {rows?.map((row, rowIndex) => (
              <div
                key={getKey(row, rowIndex)}
                className="cpt__row"
                style={{ gridTemplateColumns: gridCols, cursor: onRowClick ? "pointer" : "default" }}
                onClick={() => onRowClick?.(row)}
              >
                {columns?.map((col) => (
                  <div key={col?.key} className="cpt__cell">
                    {col?.render ? col?.render(row, rowIndex) : row[col?.key] ?? "N/A"}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination footer */}
      {showPagination && (
        <div className="cpt__pagination">
          <span className="cpt__pagination-info">
            {start}–{end} of {totalResults}
          </span>
          <div className="cpt__pagination-btns">
            <button
              className="cpt__pagination-btn"
              onClick={() => onPageChange?.(page - 1)}
              disabled={page === 0 || loading}
              aria-label="Previous page"
            >
              <ChevronLeftIcon width={16} height={16} />
            </button>
            <button
              className="cpt__pagination-btn"
              onClick={() => onPageChange?.(page + 1)}
              disabled={end >= totalResults || loading}
              aria-label="Next page"
            >
              <ChevronRightIcon width={16} height={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPaginatedTable;
